import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { Button, DialogOverlay, DialogOverlayActions, DialogOverlayFooter, Paragraph, FlowGrid } from '@fs/zion-ui'
import { getNestedOrFloatingElementById, getPersonName, useSlsStateContext } from '@fs/zion-sls'
import ZionForm, { useFieldArray, useZionForm } from '@fs/zion-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { getPersonsAndRelationshipsForHousehold, prepareRelationshipsFields } from '../utils/familyMember.utils'
import { enrichRelationshipFields } from '../utils/relationshipMap.utils'
import RelationshipInputs from './RelationshipInputs'
import SimpleRelationshipsList from './SimpleRelationshipsList'
import { previousRelationshipsValidationSchema } from '../utils/validationSchema'

/**
 * PreviousRelationshipDialogOverlay
 *
 * Shown when a user attempts to move a person to a new record group and that person
 * has existing relationships. The gets informed about the existing relationships that
 * will be removed and can add new relationships in the new record group.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.overlay - Overlay state object from useOverlay
 * @param {string} props.personIdToReview - ID of the person whose relationships are being reviewed
 * @param {Function} props.onSave - Callback when user saves the changes
 * @param {Function} props.onCancel - Callback when user cancels the dialog
 * @param {Array} props.allRecords - All records data
 * @param {string} props.targetRecordId - ID of the target record group
 * @param {string} props.currentFocusedPersonId - ID of the currently focused person
 * @param {Array} props.relationshipSuggestions - Suggestions for relationship types
 * @param {Array} props.updatedRelationships - Array of the current relationships from the form
 * @param {import('../hooks/useRelationshipsMap').RelationshipsMapApi} props.relationshipsMapApi - API for managing relationships map
 * @param {Function} props.onRelationshipChange - Callback when a relationship is changed
 */
const PreviousRelationshipDialogOverlay = ({
  overlay,
  personIdToReview,
  onSave,
  onCancel,
  allRecords,
  targetRecordId,
  currentFocusedPersonId,
  relationshipSuggestions,
  updatedRelationships = [],
  relationshipsMapApi,
  onRelationshipChange = () => {},
}) => {
  const [t] = useTranslation()
  const { images, currentImageArk } = useSlsStateContext()

  // Get the person and their relationships
  const person = useMemo(() => {
    if (!personIdToReview || !images) return null
    return getNestedOrFloatingElementById(images, personIdToReview)
  }, [images, personIdToReview])

  const personName = useMemo(() => {
    if (!person) return ''
    return getPersonName({ person, justOneName: true })
  }, [person])

  // Determine included and excluded person IDs based on updatedRelationships
  // Excluded: those with isDeleted or the current focused person and the person being reviewed
  const excludedPersonIds = useMemo(() => {
    return updatedRelationships
      .filter((rel) => rel.isDeleted || [currentFocusedPersonId, personIdToReview].includes(rel.personId))
      .map((rel) => rel.personId)
  }, [updatedRelationships, currentFocusedPersonId, personIdToReview])

  // Included: those not deleted, new persons and either not the current focused person or the person being reviewed
  const includedPersonIds = useMemo(() => {
    return updatedRelationships
      .filter(
        (rel) =>
          (!rel.isDeleted && rel.recordId !== targetRecordId && rel.personId !== personIdToReview) ||
          rel.personId?.includes('NEW_PERSON')
      )
      .map((rel) => rel.personId)
  }, [updatedRelationships, targetRecordId, personIdToReview])

  const { relationships: householdRelationships } = getPersonsAndRelationshipsForHousehold({
    person,
    images,
    allRecords,
    recordId: targetRecordId,
    currentPartitionKey: currentImageArk,
    excludedPersonIds,
    includedPersonIds,
    newlyCreatedRelationships: updatedRelationships,
  })

  const processedRelationshipFields = useMemo(() => {
    const { relationships: relationshipFields } = prepareRelationshipsFields(householdRelationships)

    // Filter out the current focused person
    const filteredFields = relationshipFields.filter((rel) => rel.personId !== currentFocusedPersonId)

    // Enrich with data from relationshipsMapApi if available
    const enrichedFields = enrichRelationshipFields({
      fields: filteredFields,
      relationshipsMapApi,
      personIdToReview,
      relationshipSuggestions,
    })

    return {
      relationships: enrichedFields,
    }
  }, [householdRelationships, currentFocusedPersonId, personIdToReview, relationshipSuggestions, relationshipsMapApi])

  const formMethods = useZionForm({
    mode: 'all',
    defaultValues: processedRelationshipFields,
    resolver: yupResolver(previousRelationshipsValidationSchema),
  })

  const fieldArrayMethods = useFieldArray({
    name: 'relationships',
    control: formMethods.control,
  })

  const { fields } = fieldArrayMethods

  const handleSubmit = useCallback(
    (data) => {
      // Pass form data to parent - dispatches will be generated from the relationships map later
      onSave?.({ formData: data, personIdToReview })
    },
    [onSave, personIdToReview]
  )

  const handleCancel = useCallback(() => {
    // Clear any temporary relationships in the map
    relationshipsMapApi.clearRelationships()

    // Invoke callback from parent
    onCancel?.()
    overlay.close()
  }, [onCancel, overlay, relationshipsMapApi])

  return (
    <DialogOverlay
      {...overlay}
      size="lg"
      title={t(
        'household-details-card.previous-relationship-dialog.relationships-title',
        'Previous Relationships for {personName}',
        { personName }
      )}
      data-testid="previous-relationship-dialog-overlay"
      as={ZionForm}
      methods={formMethods}
      onSubmit={handleSubmit}
      closeOnClickAway={false}
      nonClosable
    >
      <FlowGrid gutters="xs" orientation="vertical" columnCount={1}>
        <SimpleRelationshipsList
          person={person}
          pluralTopElement={
            <Paragraph size="sm">
              {t(
                'household-details-card.previous-relationship-dialog.remove-relationships-message-plural',
                '{personName} is in another household with existing relationships. To move to this household, the following relationships will be removed:',
                {
                  personName,
                }
              )}
            </Paragraph>
          }
          singularTopElement={
            <Paragraph size="sm">
              {t(
                'household-details-card.previous-relationship-dialog.remove-relationships-message-singular',
                '{personName} is in another household with existing relationships. To move to this household, the following relationship will be removed:',
                {
                  personName,
                }
              )}
            </Paragraph>
          }
        />
        {fields.length > 0 && (
          <FlowGrid gutters="xs" orientation="vertical" columnCount={1}>
            <Paragraph size="sm">
              {t(
                'household-details-card.previous-relationship-dialog.another-relationships-message',
                'Does {personName} have a relationship with any of the other members in this record group? ',
                {
                  personName,
                }
              )}
            </Paragraph>
            {fields.map((field, index) => (
              <RelationshipInputs
                key={field?.id}
                field={field}
                index={index}
                namesAutoSuggestions={[]}
                formMethods={formMethods}
                fieldArrayMethods={fieldArrayMethods}
                onRelationshipChange={onRelationshipChange}
                dialogMode
              />
            ))}
          </FlowGrid>
        )}
      </FlowGrid>
      <DialogOverlayFooter>
        <DialogOverlayActions>
          <Button emphasis="low" onClick={handleCancel} data-testid="previous-relationship-dialog-cancel-button">
            {t('household-details-card.previous-relationship-dialog.cancel', 'Cancel')}
          </Button>
          <Button emphasis="high" type="submit" color="red" data-testid="previous-relationship-dialog-save-button">
            {t('household-details-card.previous-relationship-dialog.save', 'Save')}
          </Button>
        </DialogOverlayActions>
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}

PreviousRelationshipDialogOverlay.propTypes = {
  overlay: PropTypes.shape({
    isOpen: PropTypes.bool,
    open: PropTypes.func,
    close: PropTypes.func.isRequired,
  }).isRequired,
  personIdToReview: PropTypes.string,
  allRecords: PropTypes.arrayOf(PropTypes.shape({})),
  targetRecordId: PropTypes.string,
  currentFocusedPersonId: PropTypes.string,
  relationshipSuggestions: PropTypes.arrayOf(PropTypes.shape({})),
  updatedRelationships: PropTypes.arrayOf(PropTypes.shape({})),
  relationshipsMapApi: PropTypes.shape({
    findRelationship: PropTypes.func,
    addOrUpdateRelationship: PropTypes.func,
    removeRelationship: PropTypes.func,
    getRelationship: PropTypes.func,
    getAllRelationships: PropTypes.func,
    getRelationshipsForPerson: PropTypes.func,
    clearRelationships: PropTypes.func,
  }),
  onRelationshipChange: PropTypes.func,
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
}

PreviousRelationshipDialogOverlay.defaultProps = {
  personIdToReview: null,
  allRecords: [],
  targetRecordId: null,
  currentFocusedPersonId: null,
  relationshipSuggestions: [],
  updatedRelationships: [],
  relationshipsMapApi: null,
  onRelationshipChange: () => {},
  onSave: () => {},
  onCancel: () => {},
}

export default PreviousRelationshipDialogOverlay
